import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaFilter, FaEye, FaPrint } from 'react-icons/fa';

const InterestCalculationSheet = () => {
  const navigate = useNavigate();
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    contractDateFrom: '',
    contractDateTo: '',
    filterClosingDate: false,
    filterClosingDateEntered: false,
    filterStorageInterest: false, // ✅ NEW FILTER
    filterDnMismatch: false,
    filterDnLessThanStorageInterest: false,
    filterLastDnDateInCurrentMonth: false
  });
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [allPagesTotals, setAllPagesTotals] = useState({ 
    totalQty: 0, 
    totalAmount: 0,
    totalStorageInterest: 0
  });
  const limit = 50;
  const reportAbortController = useRef(null);
  const totalsAbortController = useRef(null);

  // Fetch totals for all pages (for grand total calculation)
  const fetchAllPagesTotals = useCallback(async () => {
    if (totalsAbortController.current) {
      totalsAbortController.current.abort();
    }
    totalsAbortController.current = new AbortController();

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', 1);
      queryParams.append('limit', 999999);
      
      if (search) {
        queryParams.append('search', search);
      }
      if (filters.contractDateFrom) {
        queryParams.append('contractDateFrom', filters.contractDateFrom);
      }
      if (filters.contractDateTo) {
        queryParams.append('contractDateTo', filters.contractDateTo);
      }
      if (filters.filterClosingDate) {
        queryParams.append('filterClosingDate', 'true');
      }
      if (filters.filterClosingDateEntered) {
        queryParams.append('filterClosingDateEntered', 'true');
      }
      if (filters.filterStorageInterest) {
        queryParams.append('filterStorageInterest', 'true'); // ✅ NEW
      }
      if (filters.filterDnMismatch) {
        queryParams.append('filterDnMismatch', 'true');
      }
      if (filters.filterDnLessThanStorageInterest) {
        queryParams.append('filterDnLessThanStorageInterest', 'true');
      }
      if (filters.filterLastDnDateInCurrentMonth) {
        queryParams.append('filterLastDnDateInCurrentMonth', 'true');
      }

      const url = `${API_BASE_URL}/api/report/interest-calculation-sheet?${queryParams.toString()}`;

      const response = await fetch(url, {
        credentials: 'include',
        signal: totalsAbortController.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Use backend totals if available
      if (data.success && data.totals) {
        setAllPagesTotals({
          totalQty: parseFloat(data.totals.totalQty || 0),
          totalAmount: parseFloat(data.totals.totalAmount || 0),
          totalStorageInterest: parseFloat(data.totals.totalStorageInterest || 0)
        });
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error fetching totals:', error);
    }
  }, [search, filters.contractDateFrom, filters.contractDateTo, filters.filterClosingDate, filters.filterClosingDateEntered, filters.filterStorageInterest, filters.filterDnMismatch, filters.filterDnLessThanStorageInterest, filters.filterLastDnDateInCurrentMonth]);

  // Fetch report data
  const fetchReportData = useCallback(async () => {
    if (reportAbortController.current) {
      reportAbortController.current.abort();
    }
    reportAbortController.current = new AbortController();

    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      
      if (search) {
        queryParams.append('search', search);
      }
      if (filters.contractDateFrom) {
        queryParams.append('contractDateFrom', filters.contractDateFrom);
      }
      if (filters.contractDateTo) {
        queryParams.append('contractDateTo', filters.contractDateTo);
      }
      if (filters.filterClosingDate) {
        queryParams.append('filterClosingDate', 'true');
      }
      if (filters.filterClosingDateEntered) {
        queryParams.append('filterClosingDateEntered', 'true');
      }
      if (filters.filterStorageInterest) {
        queryParams.append('filterStorageInterest', 'true'); // ✅ NEW
      }
      if (filters.filterDnMismatch) {
        queryParams.append('filterDnMismatch', 'true');
      }
      if (filters.filterDnLessThanStorageInterest) {
        queryParams.append('filterDnLessThanStorageInterest', 'true');
      }
      if (filters.filterLastDnDateInCurrentMonth) {
        queryParams.append('filterLastDnDateInCurrentMonth', 'true');
      }

      const url = `${API_BASE_URL}/api/report/interest-calculation-sheet?${queryParams.toString()}`;

      const response = await fetch(url, {
        credentials: 'include',
        signal: reportAbortController.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setReportData(data.data);
        const newTotalPages = data.pagination?.totalPages || 1;
        setTotalPages(newTotalPages);
        
        // If on last page, fetch all pages totals
        if (page === newTotalPages) {
          fetchAllPagesTotals();
        }
      } else {
        toast.error(data.error || 'Failed to fetch report');
      }
      setLoading(false);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error fetching report:', error);
      toast.error('Server Error');
      setLoading(false);
    }
  }, [page, search, filters.contractDateFrom, filters.contractDateTo, filters.filterClosingDate, filters.filterClosingDateEntered, filters.filterStorageInterest, filters.filterDnMismatch, filters.filterDnLessThanStorageInterest, filters.filterLastDnDateInCurrentMonth, limit, fetchAllPagesTotals]);

  // Auto-load on mount and when filters change
  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  // Fetch totals when filters/search change
  useEffect(() => {
    fetchAllPagesTotals();
  }, [fetchAllPagesTotals]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(1);
  };

  const handleToggleChange = (field, checked) => {
    setFilters(prev => {
      const nextFilters = { ...prev, [field]: checked };
      if (field === 'filterClosingDate' && checked) {
        nextFilters.filterClosingDateEntered = false;
      } else if (field === 'filterClosingDateEntered' && checked) {
        nextFilters.filterClosingDate = false;
      }
      return nextFilters;
    });
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      contractDateFrom: '',
      contractDateTo: '',
      filterClosingDate: false,
      filterClosingDateEntered: false,
      filterStorageInterest: false, // ✅ RESET NEW FILTER
      filterDnMismatch: false,
      filterDnLessThanStorageInterest: false,
      filterLastDnDateInCurrentMonth: false
    });
    setSearch('');
    setPage(1);
  };

  const handleView = (item) => {
    navigate(`/report/interest-calculation-sheet/${item.sales_contract_id}`, {
      state: {
        commodity_name: item.commodity,
        buyer_name: item.buyer_name
      }
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 15mm 10mm;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .no-print {
            display: none !important;
          }
          
          .shadow-md {
            box-shadow: none !important;
          }
          
          table {
            width: 100%;
            border-collapse: collapse !important;
            page-break-inside: auto;
          }
          
          tr {
            page-break-inside: avoid !important;
            page-break-after: auto;
            break-inside: avoid !important;
          }
          
          thead {
            display: table-header-group !important;
          }
          
          tbody tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          .bg-blue-100 {
            background-color: #dbeafe !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .bg-gray-50 {
            background-color: #f9fafb !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>

      <div className="bg-white shadow-md p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 no-print">
          <h2 className="text-2xl font-semibold text-gray-800">
            Interest Calculation Sheet
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-[#3899cf] rounded-md hover:bg-blue-200 transition"
            >
              <FaFilter />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            <button
              onClick={handlePrint}
              disabled={loading || reportData.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-[#3899cf] text-white rounded hover:bg-[#0e8dc6] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaPrint />
              Print Report
            </button>
          </div>
        </div>

        {/* Filter Section */}
        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg no-print">
            <div className="flex flex-wrap gap-4 mb-4">
              {/* Search */}
              <div className="flex flex-col w-64">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Contract No, Buyer, Commodity..."
                  className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Contract Date From */}
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Contract Date From</label>
                <input
                  type="date"
                  value={filters.contractDateFrom}
                  onChange={(e) => handleFilterChange('contractDateFrom', e.target.value)}
                  className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Contract Date To */}
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Contract Date To</label>
                <input
                  type="date"
                  value={filters.contractDateTo}
                  onChange={(e) => handleFilterChange('contractDateTo', e.target.value)}
                  className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Reset Button */}
              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                >
                  Reset Filters
                </button>
              </div>
            </div>

            {/* Toggle Filters */}
            <div className="flex flex-wrap gap-6 mt-4 pt-4 border-t border-gray-300">
              {/* Closing Date Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.filterClosingDate}
                    onChange={(e) => handleToggleChange('filterClosingDate', e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Closing Date (Show records where Closing Date is not entered)
                  </span>
                </label>
              </div>

              {/* Closing Date Entered Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.filterClosingDateEntered}
                    onChange={(e) => handleToggleChange('filterClosingDateEntered', e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Closing Date (Show records where Closing Date is entered)
                  </span>
                </label>
              </div>

              {/* ✅ NEW: Storage + Interest > 0 Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.filterStorageInterest}
                    onChange={(e) => handleToggleChange('filterStorageInterest', e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Storage + Interest {'>'}  0 (Exclude zero values)
                  </span>
                </label>
              </div>

              {/* DN Mismatch Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.filterDnMismatch}
                    onChange={(e) => handleToggleChange('filterDnMismatch', e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Total DN Amount of system ≠ Total DN Amount of accounts
                  </span>
                </label>
              </div>

              {/* DN Less Than Storage + Interest Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.filterDnLessThanStorageInterest}
                    onChange={(e) => handleToggleChange('filterDnLessThanStorageInterest', e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Total DN Amount + 1 {"<"} Storage + Interest
                  </span>
                </label>
              </div>

              {/* Last DN Date in Current Month Toggle */}
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.filterLastDnDateInCurrentMonth}
                    onChange={(e) => handleToggleChange('filterLastDnDateInCurrentMonth', e.target.checked)}
                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    Last DN Date {"<"} Immediate Past Month-End Date (or No DN Entry)
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center p-8 text-gray-500">
            Loading report data...
          </div>
        )}

        {/* Report Table */}
        {!loading && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-200 rounded">
              <thead className="bg-blue-100 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium border">Contract No</th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium border">Date</th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium border">Name of buyer</th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium border">Commodity</th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">Qty</th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">Rate</th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">Total Amount</th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">Due Date</th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">Delay no Days</th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">Storage + Interest</th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border no-print">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reportData.length > 0 ? (
                  <>
                    {reportData.map((item, index) => {
                      const totalStorageInterest = parseFloat(item.all_months_total || 0);
                      return (
                        <tr key={index} className="hover:bg-blue-50 transition duration-200 ease-in-out">
                          <td className="px-4 py-2 border font-medium text-gray-800">
                            {item.contract_no}
                          </td>
                          <td className="px-4 py-2 border text-gray-700">
                            {item.date}
                          </td>
                          <td className="px-4 py-2 border text-gray-700">
                            {item.buyer_name}
                          </td>
                          <td className="px-4 py-2 border text-gray-700">
                            {item.commodity}
                          </td>
                          <td className="px-4 py-2 text-right border text-gray-700">
                            {item.qty}
                          </td>
                          <td className="px-4 py-2 text-right border text-gray-700">
                            {item.rate}
                          </td>
                          <td className="px-4 py-2 text-right border font-semibold text-gray-800">
                            {parseFloat(item.total_amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-2 text-center border text-gray-700">
                            {item.due_date}
                          </td>
                          <td className="px-4 py-2 text-center border text-gray-700">
                            {item.delay_days}
                          </td>
                        
<td className="px-6 py-4 text-sm">
  {totalStorageInterest.toLocaleString('en-IN', { 
    maximumFractionDigits: 2 
  })}
</td>

                          <td className="px-4 py-2 text-center border no-print">
                            <button
                              onClick={() => handleView(item)}
                              className="px-3 py-1 bg-[#3899cf] text-white rounded hover:bg-[#0e8dc6] transition text-sm flex items-center gap-1 mx-auto"
                            >
                              <FaEye />
                              VIEW
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {/* Totals row - Show on last page */}
                    {page === totalPages && (
                      <tr className="bg-gray-50 font-bold">
                        <td colSpan="4" className="px-4 py-3 border text-right text-gray-700">
                          Total:
                        </td>
                        <td className="px-4 py-3 text-right border text-gray-800">
                          {allPagesTotals.totalQty.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-right border text-gray-800">
                          -
                        </td>
                        <td className="px-4 py-3 text-right border text-gray-800">
                          {allPagesTotals.totalAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td colSpan="2" className="px-4 py-3 border"></td>
                        <td className="px-4 py-3 text-right border text-gray-800">
                          {allPagesTotals.totalStorageInterest.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-3 border no-print"></td>
                      </tr>
                    )}
                  </>
                ) : (
                  <tr>
                    <td colSpan="11" className="text-center p-8 text-gray-500 italic">
                      No data found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && reportData.length > 0 && totalPages > 1 && (
          <div className="flex justify-end items-center gap-2 mt-3 text-xs no-print">
            <button
              onClick={handlePrev}
              disabled={page === 1}
              className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
            >
              Prev
            </button>
            <span className="text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={handleNext}
              disabled={page === totalPages}
              className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}
            >
              Next
            </button>
          </div>
        )}

        <ToastContainer 
          position="top-right" 
          autoClose={3000} 
          hideProgressBar={false} 
          newestOnTop 
          closeOnClick 
          pauseOnFocusLoss 
          draggable 
          pauseOnHover 
          className="no-print"
        />
      </div>
    </>
  );
};

export default InterestCalculationSheet;
